/**
 * DocsSearchPanel — right-side Salesforce docs search box.
 *
 * Fast route: type query → Enter or click → backend searches Exa +
 * pgvector → Haiku synthesizes → answer appears in chat instantly.
 * Does NOT touch runStatus or interfere with agent SSE state.
 */
import { useCallback, useRef, useState } from "react";
import { Search, Loader2, Send } from "lucide-react";
import toast from "react-hot-toast";
import { searchDocsInChat } from "@/services/docs/docsService";
import { useDevAgentStore } from "@/stores/devAgentStore";

interface DocsSearchPanelProps {
  sessionUuid: string | null;
  onMessagePosted?: () => void;
}

export function DocsSearchPanel({ sessionUuid, onMessagePosted }: DocsSearchPanelProps) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const searchingRef = useRef(false);

  const isRunning = useDevAgentStore((s) => s.runStatus === "running");
  const findLastMessageIndex = useCallback(
    (items: Array<{ data?: { messageUuid?: string } }>, messageUuid: string) => {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i]?.data?.messageUuid === messageUuid) return i;
      }
      return -1;
    },
    [],
  );

  const handleSearch = useCallback(async () => {
    const q = query.trim();
    if (!q || q.length < 2) return;
    if (!sessionUuid) {
      toast.error("No active chat session");
      return;
    }
    if (searchingRef.current || isRunning) return;
    searchingRef.current = true;
    setLoading(true);

    const thinkingUuid = crypto.randomUUID();
    const userUuid = crypto.randomUUID();
    const store = useDevAgentStore.getState();

    store.addFeedItem({
      type: "chat_user",
      data: { content: q, messageUuid: userUuid },
    });
    store.addFeedItem({
      type: "chat_agent",
      data: { content: "_Searching Salesforce docs…_", messageUuid: thinkingUuid },
    });

    try {
      const resp = await searchDocsInChat(sessionUuid, q);
      const items = useDevAgentStore.getState().feedItems;
      const idx = findLastMessageIndex(items, thinkingUuid);
      if (idx >= 0) {
        useDevAgentStore.getState().updateFeedItem(items[idx].id, {
          content: resp.assistant_message.content,
          messageUuid: resp.assistant_message.uuid,
        });
      }
      setQuery("");
      onMessagePosted?.();
    } catch (err) {
      console.error("[DocsSearchPanel] search failed:", err);
      toast.error("Docs search failed");
      const items = useDevAgentStore.getState().feedItems;
      const idx = findLastMessageIndex(items, thinkingUuid);
      if (idx >= 0) {
        useDevAgentStore.getState().updateFeedItem(items[idx].id, {
          content: "_Search failed — try again._",
        });
      }
    } finally {
      setLoading(false);
      searchingRef.current = false;
    }
  }, [sessionUuid, query, onMessagePosted, isRunning, findLastMessageIndex]);

  return (
    <div className="p-3 flex flex-col gap-2">
      <div className="relative flex gap-1.5">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !loading) {
                e.preventDefault();
                handleSearch();
              }
            }}
            placeholder="Search Salesforce docs…"
            disabled={loading || isRunning}
            className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md bg-card border-2 border-border shadow-sm outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 placeholder:text-muted-foreground disabled:opacity-50"
          />
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={loading || isRunning || query.trim().length < 2}
          className="px-2.5 py-1.5 rounded-md border-2 border-primary/80 bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:border-primary transition-colors disabled:opacity-45 disabled:cursor-not-allowed shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          title="Send docs search to chat"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
      {!loading && (
        <p className="text-[10px] text-muted-foreground px-1">
          Apex, LWC, SOQL, APIs, limits, best practices, release notes
        </p>
      )}
      {loading && (
        <p className="text-[10px] text-muted-foreground font-medium px-1">
          Searching docs & synthesizing answer…
        </p>
      )}
    </div>
  );
}
